/**
 * defaultRail.tsx
 */
import AIIcon from '@components/atoms/icons/AI';
import ArrowsSelector from '../nav/arrowsSelector';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import MainContext from '@shared/components/utils/context/mainContext';
import Rail from '@shared/classes/rail';
import RailDataContainer from '@components/atoms/container/railDataContainer';
import RailItemContainer from '@components/atoms/container/railItemContainer';
import RailTitle from '@components/atoms/text/railTitle';
import useAnchor from '../../lib/useAnchor';
import useCard from '@components/sections/rails/lib/useCard';
import useRail from '../../lib/useRail';
import useStableSelectHandler from '../../lib/useStableSelectHandler';
import useUpdateEvents from '@components/sections/rails/lib/useUpdateEvents';
import { memo, useContext } from 'react';
import { RailDisplayMode } from '@shared/types/enums/rail';

/**
 * Scale of the AI icon.
 */
const AI_ICON_SCALE = 0.8;
/**
 * Maximum number of items to display in the default rail.
 */
const MAX_EPG_ITEMS = 150;
const MAX_HORIZONTAL_ITEMS = 5;
const MAX_SQUARE_ITEMS = 20;
const MAX_VERTICAL_ITEMS = 9;

/**
 * Default rail component. Conditionally displays a rail with a title and a list of items.
 * Memoized on `rail` identity so navigating within one rail doesn't
 * re-render every other rail on the page.
 */
function DefaultRail({ rail }: { rail: Rail }) {
  const { config, selectAsset } = useContext(MainContext);
  const { Anchor } = useAnchor(rail);
  const { Card, vertical } = useCard(rail);
  const { focused, hovered, hIndex, vIndex, currentRail, callbacks } = useRail(
    rail.id,
    Focus.RAILS_LIST
  );
  const getClickHandler = useStableSelectHandler(rail.id, selectAsset);
  const data = useUpdateEvents(rail);
  const renderRail =
    vIndex >= currentRail - config.threshold.rear &&
    vIndex <= currentRail + config.threshold.front;
  return renderRail ? (
    <RailItemContainer
      displayMode={rail.displayMode}
      hideRail={currentRail > vIndex}
      {...callbacks}
    >
      {/* Rail title */}
      {rail.title ? (
        <RailTitle>
          {rail.reelIA ? <AIIcon scale={AI_ICON_SCALE} /> : ''}
          {rail.title}
        </RailTitle>
      ) : null}
      {/* Rail content */}
      <div>
        {/* Anchor */}
        {rail.anchor && Anchor ? <Anchor asset={rail.anchor} /> : null}
        {/* Data */}
        <RailDataContainer displayMode={rail.displayMode}>
          {(data as Event[])?.map((item, idx) => {
            if (!item) return null;
            const limit =
              rail.displayMode === RailDisplayMode.EPG
                ? MAX_EPG_ITEMS
                : rail.displayMode === RailDisplayMode.SQUARE
                ? MAX_SQUARE_ITEMS
                : vertical
                ? MAX_VERTICAL_ITEMS
                : MAX_HORIZONTAL_ITEMS;
            const display = Math.abs(idx - hIndex) < limit;
            const hideItem = hIndex > idx;
            return display ? (
              <Card
                key={`${item?.id}-${idx}`}
                item={item}
                hide={hideItem}
                focused={focused && hIndex === idx}
                onClick={getClickHandler(idx)}
              />
            ) : null;
          })}
        </RailDataContainer>
      </div>
      {/* Navigation arrows */}
      <ArrowsSelector hovered={hovered} hIndex={hIndex} rail={rail} />
    </RailItemContainer>
  ) : null;
}

export default memo(
  DefaultRail,
  // Default shallow comparison misses category filtering: `useFilter`
  // mutates `rail.data` in place rather than swapping the `rail` object
  // itself (it can be Immer-frozen), so `rail.data` must be compared
  // explicitly or this rail never re-renders when the filter changes it.
  (prev, next) => prev.rail === next.rail && prev.rail.data === next.rail.data
);
